/**
 * Support Notifications — global unread tracking + in-app toast for the
 * Commonplace SUPPORT (agent) channel ONLY.
 *
 * This is intentionally separate from the per-order (Buyer/Seller via Quo/
 * OpenPhone) message threads. Support messages live on the agent channel
 * (`/messages/support`, recipientType/senderType = AGENT) and surface only in
 * the Support inbox. Per-order messages live in MessagingScreen and never feed
 * this provider.
 *
 * Detection of a "new support message":
 *   - Poll GET /messages/support (~8s, the same endpoint the inbox uses).
 *   - An inbound support message is one with senderType === 'AGENT'
 *     (the support team / admin → driver). Driver-sent messages
 *     (senderType DRIVER, recipientType AGENT) are ignored.
 *   - We persist the timestamp of the newest inbound message we've ALREADY
 *     accounted for in `@cp_support_last_seen_ts`. Anything newer than that is
 *     "new": it bumps the unread badge count and, if it arrived since the last
 *     poll (not on the very first load), fires a one-shot toast.
 *   - Opening the Support inbox calls markAllRead(), which advances the
 *     last-seen pointer to "now" and zeroes the badge.
 */
import React, { createContext, useCallback, useContext, useEffect, useRef, useState } from 'react';
import AsyncStorage from '@react-native-async-storage/async-storage';
import { useSelector } from 'react-redux';
import { RootState } from '../store';
import { api } from './api';

const LAST_SEEN_KEY = '@cp_support_last_seen_ts';
const POLL_MS = 8000;

interface SupportMessage {
  id: string;
  senderType: string;
  recipientType: string;
  messageText?: string;
  timestamp: string;
}

interface SupportNotificationsValue {
  /** Number of inbound support messages newer than the last time the inbox was opened. */
  unreadCount: number;
  /** Most recent inbound support toast text (null when nothing pending). */
  toast: { message: string; key: number } | null;
  /** Clears the active toast. */
  clearToast: () => void;
  /** Marks every support message read — advances the last-seen pointer, zeroes the badge. */
  markAllRead: () => void;
  /** Force an immediate refresh (used by the inbox after it loads). */
  refresh: () => void;
}

const SupportNotificationsContext = createContext<SupportNotificationsValue>({
  unreadCount: 0,
  toast: null,
  clearToast: () => {},
  markAllRead: () => {},
  refresh: () => {},
});

export function useSupportNotifications() {
  return useContext(SupportNotificationsContext);
}

const ts = (s: string) => {
  const t = new Date(s).getTime();
  return Number.isFinite(t) ? t : 0;
};

export function SupportNotificationsProvider({ children }: { children: React.ReactNode }) {
  const token = useSelector((s: RootState) => s.auth.token);

  const [unreadCount, setUnreadCount] = useState(0);
  const [toast, setToast] = useState<{ message: string; key: number } | null>(null);

  // Timestamp of the newest inbound message the driver has already "seen"
  // (i.e. before this point counts as read). Hydrated from AsyncStorage.
  const lastSeenTsRef = useRef<number>(0);
  // Timestamp of the newest inbound message observed in the PREVIOUS poll, used
  // to detect arrivals between polls so we only toast genuinely new messages.
  const lastObservedTsRef = useRef<number>(0);
  const hydratedRef = useRef(false);
  const firstLoadRef = useRef(true);

  const clearToast = useCallback(() => setToast(null), []);

  const markAllRead = useCallback(() => {
    // Use the newest SERVER timestamp we've observed (not the local clock) as the read
    // pointer — a phone clock behind the server would otherwise mark a just-arrived message
    // as already-read (it'd never badge). Fall back to local now if no poll has run yet.
    const seen = Math.max(lastObservedTsRef.current, Date.now());
    lastSeenTsRef.current = seen;
    lastObservedTsRef.current = seen;
    setUnreadCount(0);
    AsyncStorage.setItem(LAST_SEEN_KEY, String(seen)).catch(() => {});
  }, []);

  const poll = useCallback(async () => {
    if (!token) return;
    try {
      const { data } = await api.get('/messages/support');
      if (!Array.isArray(data)) return;

      // Inbound support = from the agent/support team to the driver.
      const inbound = (data as SupportMessage[]).filter(m => m.senderType === 'AGENT');

      // Unread = inbound newer than the last time the inbox was opened.
      const unread = inbound.filter(m => ts(m.timestamp) > lastSeenTsRef.current);
      setUnreadCount(unread.length);

      // Newest inbound timestamp overall.
      const newestTs = inbound.reduce((max, m) => Math.max(max, ts(m.timestamp)), 0);
      const newestMsg = inbound.reduce<SupportMessage | null>(
        (acc, m) => (acc == null || ts(m.timestamp) >= ts(acc.timestamp) ? m : acc),
        null,
      );

      // Toast only for a genuinely NEW inbound message: one that arrived after
      // our previous observation AND is still unread. Never on the first load.
      if (!firstLoadRef.current && newestTs > lastObservedTsRef.current && newestTs > lastSeenTsRef.current && newestMsg) {
        const preview = (newestMsg.messageText || 'New support message').trim();
        setToast({
          message: preview.length > 90 ? preview.slice(0, 87) + '…' : preview,
          key: Date.now(),
        });
      }

      lastObservedTsRef.current = Math.max(lastObservedTsRef.current, newestTs);
      firstLoadRef.current = false;
    } catch {
      /* offline / transient — keep last known state */
    }
  }, [token]);

  // Hydrate the last-seen pointer once.
  useEffect(() => {
    AsyncStorage.getItem(LAST_SEEN_KEY)
      .then(raw => {
        const v = raw ? Number(raw) : 0;
        if (Number.isFinite(v)) {
          lastSeenTsRef.current = v;
          lastObservedTsRef.current = v;
        }
      })
      .catch(() => {})
      .finally(() => { hydratedRef.current = true; });
  }, []);

  // Poll while authenticated. Reset transient state on logout.
  useEffect(() => {
    if (!token) {
      setUnreadCount(0);
      setToast(null);
      firstLoadRef.current = true;
      return;
    }
    let cancelled = false;
    const tick = () => { if (!cancelled) poll(); };
    // Wait for hydration before the first poll so we don't mis-toast.
    const start = setTimeout(tick, hydratedRef.current ? 0 : 300);
    const interval = setInterval(tick, POLL_MS);
    return () => {
      cancelled = true;
      clearTimeout(start);
      clearInterval(interval);
    };
  }, [token, poll]);

  const refresh = useCallback(() => { poll(); }, [poll]);

  return (
    <SupportNotificationsContext.Provider
      value={{ unreadCount, toast, clearToast, markAllRead, refresh }}
    >
      {children}
    </SupportNotificationsContext.Provider>
  );
}
